import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsWhatsapp, BsPhone, BsGeoAlt, BsCalendarCheck, BsX, BsShop, BsClockHistory } from 'react-icons/bs';
import { BiNote, BiUser } from 'react-icons/bi';
import { Phone, MapPinned, MessageCircle, UserPlus } from 'lucide-react';
import CustomLoading from '../../customLoading/CustomLoading';
import { getB2BLeadNotes, createB2BLeadNote, getB2BLeadWorkflowDetail, updateB2BLead, getB2BCampaigns } from '../../../services/b2bLeads';
import { toast } from 'react-toastify';
import { formatDateMonth } from '../../../utils/FormatDateTime';
import UserOrderHistory from '../../../pages/sales/UserOrderHistory';
import LeadWhatsappChat from './LeadWhatsappChat';
import { B2B_OUTCOME_MAP, B2B_PURPOSE_MAP } from '../../../constants/b2bWorkflow';
import Dropdown from '../../inputFields/DropDown';
import MultiSelectDropDown from '../../inputFields/MultiSelectDropDown';
import MultiAsyncDropdown from '../../inputFields/MultiAsyncDropDown';
import { searchProductWithAkaName } from '../../../services/search';
import axios from 'axios';
import { categoriesCRUDUrl, districtApi, stateApi, subcategoriesCRUDUrl } from '../../../constants/apis';
import { statesOfIndia } from '../../../constants/states';
import { getUserToken } from '../../../helpers/local_storage_helpers';
import { sanitizeMobile } from '../../../utils/phone';

const formatDateTime = (value) => value ? new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const formatDuration = (seconds) => {
    const value = Number(seconds || 0);
    if (!value) return '';
    const mins = Math.floor(value / 60);
    const secs = value % 60;
    return mins ? `${mins}m ${secs}s` : `${secs}s`;
};

const OutcomeBadge = ({ value }) => {
    const option = B2B_OUTCOME_MAP[value || 'new'] || B2B_OUTCOME_MAP.new;
    return <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black ${option.badgeClass}`}>{option.label}</span>;
};

const B2BLeadDetail = ({ lead, onClose, onAssignRequest, onUpdated }) => {
    const [loading, setLoading] = useState(true);
    const [workflow, setWorkflow] = useState(null);
    const [notes, setNotes] = useState([]);
    const [notesLoading, setNotesLoading] = useState(true);
    const [newNote, setNewNote] = useState('');
    const [historyOpen, setHistoryOpen] = useState(false);
    const [savingDetails, setSavingDetails] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [formDistricts, setFormDistricts] = useState([]);
    const [formStates, setFormStates] = useState([]);
    const [visitingCardFile, setVisitingCardFile] = useState(null);
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [editForm, setEditForm] = useState({
        shop_name: '',
        owner_name: '',
        phone: '',
        additional_numbers: '',
        campaign: '',
        product_category: [],
        product_subcategory: [],
        products: [],
        area: '',
        state: '',
        district: '',
        address_line1: '',
    });

    const activeLead = workflow?.lead || lead;

    useEffect(() => {
        if (lead?.id) {
            fetchDetail();
            fetchNotes();
        }
    }, [lead?.id]);

    useEffect(() => {
        fetchEditResources();
    }, []);

    useEffect(() => {
        if (!activeLead?.id) return;

        const initForm = async () => {
            const currentStateName = activeLead.state_name || activeLead.state || '';
            let resolvedStateId = '';
            let resolvedDistrictId = '';
            let districtsList = [];

            if (formStates.length > 0 && currentStateName) {
                const matchState = formStates.find(s => s.name === currentStateName || String(s.id) === String(currentStateName));
                if (matchState) resolvedStateId = matchState.id;
            }

            if (currentStateName) {
                try {
                    const res = await axios.get(`${districtApi}${currentStateName}`, { headers: { Authorization: `Token ${getUserToken()}` } });
                    if (res.status === 200) {
                        districtsList = res.data || [];
                        setFormDistricts(districtsList);
                    }
                } catch (error) {
                    console.error("Error loading districts", error);
                }
            } else {
                setFormDistricts([]);
            }

            const currentDistrictName = activeLead.district_name || activeLead.district || '';
            if (districtsList.length > 0 && currentDistrictName) {
                const matchDistrict = districtsList.find(d => d.name === currentDistrictName || String(d.id) === String(currentDistrictName));
                if (matchDistrict) resolvedDistrictId = matchDistrict.id;
            }

            setEditForm({
                shop_name: activeLead.shop_name || '',
                owner_name: activeLead.owner_name || '',
                phone: activeLead.phone || '',
                additional_numbers: activeLead.additional_numbers || '',
                campaign: activeLead.campaign?.id || activeLead.campaign || activeLead.campaign_id || '',
                product_category: (activeLead.product_category || []).map(item => item.id),
                product_subcategory: (activeLead.product_subcategory || activeLead.product_sub_category || activeLead.subcategory || activeLead.sub_category || []).map(item => item.id),
                products: (activeLead.products || []).map(item => ({
                    value: item.id,
                    label: item.aka_name || item.product_name || item.name || '',
                })),
                area: activeLead.area || '',
                state: resolvedStateId,
                district: resolvedDistrictId,
                address_line1: activeLead.address_line1 || '',
            });
            setVisitingCardFile(null);
        };

        initForm();
    }, [activeLead?.id, formStates]);

    const fetchDetail = async () => {
        setLoading(true);
        try {
            const res = await getB2BLeadWorkflowDetail(lead.id);
            if (res.status === 200) {
                setWorkflow(res.data);
            }
        } catch (error) {
            console.error("Error fetching workflow detail:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchNotes = async () => {
        setNotesLoading(true);
        try {
            const res = await getB2BLeadNotes(lead.id);
            if (res.status === 200) {
                setNotes(res.data.results || res.data || []);
            }
        } catch (error) {
            console.error("Error fetching notes:", error);
        } finally {
            setNotesLoading(false);
        }
    };

    const fetchEditResources = async () => {
        try {
            const [campaignRes, categoryRes, stateRes, subcategoryRes] = await Promise.all([
                getB2BCampaigns(),
                axios.get(categoriesCRUDUrl, { headers: { Authorization: `Token ${getUserToken()}` } }),
                axios.get(stateApi, { headers: { Authorization: `Token ${getUserToken()}` } }),
                axios.get(`${subcategoriesCRUDUrl}?page_size=1000`, { headers: { Authorization: `Token ${getUserToken()}` } }),
            ]);
            if (campaignRes.status === 200) setCampaigns(campaignRes.data.results || campaignRes.data || []);
            if (categoryRes.status === 200) setCategories(categoryRes.data.results || categoryRes.data || []);
            if (stateRes.status === 200) setFormStates(stateRes.data || []);
            if (subcategoryRes.status === 200) setSubcategories(subcategoryRes.data.results || subcategoryRes.data || []);
        } catch (error) {
            console.error("Error fetching edit resources:", error);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;
        try {
            const res = await createB2BLeadNote({ lead: lead.id, content: newNote });
            if (res.status === 201) {
                toast.success("Note added");
                setNewNote('');
                setNotes(prev => [res.data, ...prev]);
            }
        } catch (error) {
            toast.error("Error adding note");
        }
    };

    const handleStateChange = async (stateId) => {
        const selectedState = formStates.find(s => s.id === stateId);
        setEditForm(prev => ({ ...prev, state: stateId, district: '' }));
        if (selectedState) {
            try {
                const res = await axios.get(`${districtApi}${selectedState.name}`, { headers: { Authorization: `Token ${getUserToken()}` } });
                if (res.status === 200) {
                    setFormDistricts(res.data);
                }
            } catch (error) {
                setFormDistricts([]);
            }
        } else {
            setFormDistricts([]);
        }
    };

    const handleDetailsSave = async () => {
        setSavingDetails(true);
        try {
            const isEqual = (val1, val2) => {
                if (!val1 && !val2) return true;
                return String(val1).trim() === String(val2).trim();
            };

            const payload = {};

            if (!isEqual(editForm.shop_name, activeLead.shop_name)) payload.shop_name = editForm.shop_name;
            if (!isEqual(editForm.owner_name, activeLead.owner_name)) payload.owner_name = editForm.owner_name;
            
            const sanitizedPhone = sanitizeMobile(editForm.phone);
            if (!isEqual(sanitizedPhone, activeLead.phone)) payload.phone = sanitizedPhone;
            
            if (!isEqual(editForm.additional_numbers, activeLead.additional_numbers)) payload.additional_numbers = editForm.additional_numbers;
            
            const activeCampaignId = activeLead.campaign?.id || activeLead.campaign || activeLead.campaign_id || '';
            if (!isEqual(editForm.campaign, activeCampaignId)) payload.campaign = editForm.campaign || null;

            if (!isEqual(editForm.area, activeLead.area)) payload.area = editForm.area;
            if (!isEqual(editForm.address_line1, activeLead.address_line1)) payload.address_line1 = editForm.address_line1;

            // State & District ID matching (always sent if either is set to ensure backend validation is successful)
            if (editForm.state || editForm.district) {
                payload.state = editForm.state || null;
                payload.district = editForm.district || null;
            }

            // Product Category comparison
            const activeCatIds = (activeLead.product_category || []).map(item => item.id).sort().join(',');
            const currentCatIds = [...editForm.product_category].sort().join(',');
            if (activeCatIds !== currentCatIds) {
                payload.product_category = editForm.product_category;
            }

            // Product Subcategory comparison
            const activeSubCatIds = (activeLead.product_subcategory || activeLead.product_sub_category || activeLead.subcategory || activeLead.sub_category || []).map(item => item.id).sort().join(',');
            const currentSubCatIds = [...editForm.product_subcategory].sort().join(',');
            if (activeSubCatIds !== currentSubCatIds) {
                payload.product_subcategory = editForm.product_subcategory;
            }

            // Products comparison
            const activeProductIds = (activeLead.products || []).map(item => item.id).sort().join(',');
            const currentProductIds = (editForm.products || []).map(item => item.value).sort().join(',');
            if (activeProductIds !== currentProductIds) {
                payload.products = (editForm.products || []).map(item => item.value);
            }

            if (Object.keys(payload).length === 0 && !visitingCardFile) {
                toast.info("No changes to save");
                setSavingDetails(false);
                return;
            }

            let submitData;
            if (visitingCardFile || Object.keys(payload).length > 0) {
                if (visitingCardFile || Object.values(payload).some(v => Array.isArray(v))) {
                    submitData = new FormData();
                    Object.keys(payload).forEach(key => {
                        const val = payload[key];
                        if (Array.isArray(val)) {
                            val.forEach(item => {
                                submitData.append(key, item);
                            });
                        } else if (val !== null && val !== undefined) {
                            submitData.append(key, val);
                        }
                    });
                    if (visitingCardFile) {
                        submitData.append('visiting_card', visitingCardFile);
                    }
                } else {
                    submitData = payload;
                }
            }

            console.log("Saving B2B Lead details. Payload:", payload);
            const res = await updateB2BLead(activeLead.id, submitData);
            if (res.status === 200) {
                toast.success("Lead details updated");
                setWorkflow(prev => ({ ...prev, lead: { ...(prev?.lead || activeLead), ...res.data } }));
                onUpdated?.();
                fetchDetail();
            } else {
                toast.error(res.data?.detail || "Failed to update lead details");
            }
        } catch (error) {
            toast.error("Error updating lead details");
        } finally {
            setSavingDetails(false);
        }
    };

    const timeline = useMemo(() => {
        const interactions = (workflow?.interactions || []).map(item => ({
            ...item,
            type: 'interaction',
            timestamp: new Date(item.interaction_time).getTime(),
        }));
        const assignments = (workflow?.assignment_history || []).map(item => ({
            ...item,
            type: 'assignment',
            timestamp: new Date(item.assigned_at).getTime(),
        }));
        return [...interactions, ...assignments].sort((a, b) => b.timestamp - a.timestamp);
    }, [workflow]);

    const currentAssignment = activeLead?.assigned_to || (workflow?.assignment_history || []).find(item => item.is_active);

    if (!lead) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[1000] flex justify-end"
                onClick={onClose}
            >
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                    className="w-full max-w-2xl bg-gray-50 h-full shadow-2xl flex flex-col overflow-hidden"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="bg-white border-b px-4 py-3 flex justify-between items-center sticky top-0 z-10 shadow-sm">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                                <BsShop size={20} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800 leading-tight">{activeLead?.shop_name || 'Lead Detail'}</h2>
                                <p className="text-xs text-gray-500">{activeLead?.owner_name || 'No owner'}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <OutcomeBadge value={activeLead?.last_outcome} />
                            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600">
                                <BsX size={28} />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <CustomLoading size={50} />
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 pb-10">
                            <div className="flex flex-wrap gap-2 items-center">
                                <a href={`https://wa.me/${(activeLead?.phone || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-semibold hover:bg-green-600 transition-all shadow-sm">
                                    <BsWhatsapp /> WhatsApp
                                </a>
                                <a href={`tel:${activeLead?.phone}`} className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white rounded-lg text-xs font-semibold hover:bg-primary/90 transition-all shadow-sm">
                                    <BsPhone /> Call Now
                                </a>
                                <span className="text-sm font-bold text-gray-600 ml-2">{activeLead?.phone}</span>
                            </div>

                            {activeLead?.phone && <LeadWhatsappChat phone={activeLead.phone} />}

                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 text-gray-800 font-bold text-sm">
                                        <BsShop className="text-primary" /> Lead Details
                                    </div>
                                    <button
                                        onClick={handleDetailsSave}
                                        disabled={savingDetails}
                                        className="px-4 py-2 bg-primary text-white rounded-lg text-[10px] font-black disabled:bg-gray-300"
                                    >
                                        {savingDetails ? 'Saving...' : 'Save Details'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Shop Name</label>
                                        <input
                                            value={editForm.shop_name}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, shop_name: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Shop name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Owner Name</label>
                                        <input
                                            value={editForm.owner_name}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, owner_name: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Owner name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Phone</label>
                                        <input
                                            value={editForm.phone}
                                            onChange={(e) => {
                                                const sanitized = sanitizeMobile(e.target.value);
                                                e.target.value = sanitized;
                                                setEditForm(prev => ({ ...prev, phone: sanitized }));
                                            }}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Phone number"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Additional Number</label>
                                        <input
                                            value={editForm.additional_numbers}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, additional_numbers: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Name:Number, Name:Number"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Area</label>
                                        <input
                                            value={editForm.area}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, area: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                                            placeholder="Area name"
                                        />
                                    </div>
                                    <Dropdown
                                        label="State"
                                        placeholder="Select state"
                                        options={(formStates || []).map(s => ({ label: s.name, value: s.id }))}
                                        value={editForm.state}
                                        onChange={handleStateChange}
                                        needSearch
                                        compact
                                    />
                                    <Dropdown
                                        label="District"
                                        placeholder="Select district"
                                        options={(formDistricts || []).map(d => ({ label: d.name || d, value: d.id || d }))}
                                        value={editForm.district}
                                        onChange={(val) => setEditForm(prev => ({ ...prev, district: val }))}
                                        disabled={!editForm.state}
                                        needSearch
                                        compact
                                    />
                                    <Dropdown
                                        label="Campaign"
                                        options={campaigns.map(item => ({ label: item.name, value: item.id }))}
                                        value={editForm.campaign}
                                        onChange={(val) => setEditForm(prev => ({ ...prev, campaign: val }))}
                                        placeholder="Select campaign"
                                        needSearch
                                        compact
                                    />
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-gray-500 mb-1">Address</label>
                                        <textarea
                                            value={editForm.address_line1}
                                            onChange={(e) => setEditForm(prev => ({ ...prev, address_line1: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                                            placeholder="Enter address"
                                            rows={2}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <MultiSelectDropDown
                                            label="Product Categories"
                                            options={categories.map(item => ({ label: item.name, value: item.id }))}
                                            value={editForm.product_category}
                                            onChange={(val) => setEditForm(prev => ({ ...prev, product_category: val }))}
                                            placeholder="Select categories"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <MultiSelectDropDown
                                            label="Product Subcategories"
                                            options={subcategories
                                                .filter(sc => {
                                                    const catId = sc.category?.id || sc.category;
                                                    return editForm.product_category.length === 0 || editForm.product_category.map(String).includes(String(catId));
                                                })
                                                .map(item => ({ label: item.name, value: item.id }))}
                                            value={editForm.product_subcategory}
                                            onChange={(val) => setEditForm(prev => ({ ...prev, product_subcategory: val }))}
                                            placeholder="Select subcategories"
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <MultiAsyncDropdown
                                            label="Products"
                                            name="products"
                                            placeholder="Search products by aka name..."
                                            apiCallFunction={async (input) => {
                                                const response = await searchProductWithAkaName(input);
                                                const raw = response?.data?.results ?? response?.data;
                                                return (Array.isArray(raw) ? raw : []).map(pro => ({
                                                    value: pro.id,
                                                    label: pro.aka_name ?? pro.product_name ?? "Product",
                                                }));
                                            }}
                                            value={editForm.products}
                                            onChange={(val) => setEditForm(prev => ({ ...prev, products: val }))}
                                            needSearch
                                        />
                                    </div>
                                    <div className="md:col-span-2 space-y-1">
                                        <label className="block text-xs font-bold text-gray-500">Visiting Card</label>
                                        {(activeLead?.visiting_card || lead?.visiting_card) && (
                                            <div className="mb-2">
                                                <button
                                                    onClick={() => setLightboxUrl(activeLead?.visiting_card || lead?.visiting_card)}
                                                    className="inline-block hover:opacity-85 transition-opacity text-left cursor-zoom-in"
                                                >
                                                    <img
                                                        src={activeLead?.visiting_card || lead?.visiting_card}
                                                        alt="Visiting Card"
                                                        className="max-h-32 rounded-lg border object-cover"
                                                    />
                                                </button>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                            onChange={(e) => setVisitingCardFile(e.target.files[0] || null)}
                                        />
                                    </div>
                                </div>
                            </div>



                            {(workflow?.follow_ups?.pending || []).length > 0 && (
                                <div className="bg-white rounded-xl border border-amber-100 shadow-sm overflow-hidden">
                                    <div className="px-4 py-3 bg-amber-50 border-b flex items-center gap-2 text-amber-800 font-bold text-sm">
                                        <BsCalendarCheck /> Upcoming Follow-ups
                                    </div>
                                    <div className="divide-y">
                                        {workflow.follow_ups.pending.map(item => (
                                            <div key={item.id} className="p-3">
                                                <div className="flex justify-between gap-3">
                                                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black">{B2B_PURPOSE_MAP[item.purpose]?.label || item.purpose}</span>
                                                    <span className="text-xs font-bold text-gray-700">{formatDateTime(item.follow_up_at)}</span>
                                                </div>
                                                {item.remarks && <p className="text-[11px] text-gray-600 mt-2">{item.remarks}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}



                            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="px-4 py-3 bg-gray-50 border-b flex items-center gap-2 text-gray-800 font-bold text-sm">
                                    <BsClockHistory className="text-primary" /> Activity Timeline
                                </div>
                                <div className="divide-y max-h-[420px] overflow-y-auto">
                                    {timeline.length > 0 ? timeline.map((item, i) => {
                                        const isAssignment = item.type === 'assignment';
                                        const Icon = isAssignment ? BiUser : item.interaction_type === 'visit' ? MapPinned : item.interaction_type === 'whatsapp' ? MessageCircle : Phone;
                                        const dotClass = isAssignment ? 'bg-amber-100 text-amber-700' : item.interaction_type === 'visit' ? 'bg-purple-100 text-purple-700' : item.interaction_type === 'whatsapp' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700';
                                        return (
                                            <div key={`${item.type}-${item.id || i}`} className="p-3 hover:bg-gray-50 transition-colors">
                                                <div className="flex gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${dotClass}`}>
                                                        <Icon size={15} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex justify-between gap-2">
                                                            <div className="text-xs font-black text-gray-800">
                                                                {isAssignment ? `Assigned to ${item.sales_person_name}` : item.interaction_type?.replace('_', ' ')}
                                                            </div>
                                                            <span className="text-[10px] text-gray-400 whitespace-nowrap">{formatDateTime(isAssignment ? item.assigned_at : item.interaction_time)}</span>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 mt-1">
                                                            {!isAssignment && <OutcomeBadge value={item.outcome} />}
                                                            {!isAssignment && item.duration ? <span className="text-[10px] text-gray-500 font-bold">{formatDuration(item.duration)}</span> : null}
                                                            <span className="text-[10px] text-gray-500">{isAssignment ? item.assigned_by_name : item.sales_person_name}</span>
                                                        </div>
                                                        {(item.note || item.remarks) && <p className="mt-2 text-[11px] text-gray-600 bg-gray-50 border-l-2 border-primary px-2 py-1 rounded">{item.note || item.remarks}</p>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }) : (
                                        <div className="text-center py-10 text-[11px] text-gray-400 italic">No workflow activity yet.</div>
                                    )}
                                </div>
                            </div>



                            {(activeLead?.user_id || activeLead?.phone) && (
                                <div className="mt-4 border-t pt-4">
                                    <div className="flex items-center gap-2 mb-4 text-gray-800 font-bold text-sm">
                                        <BsShop className="text-emerald-500" /> Order History
                                    </div>
                                    <UserOrderHistory
                                        user_id={activeLead.user_id}
                                        mobile_number={activeLead.phone}
                                        showTopItems={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>
            </motion.div>
            {lightboxUrl && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-150" onClick={() => setLightboxUrl(null)}>
                    <div className="relative max-w-2xl max-h-[90vh] bg-white rounded-2xl p-2 shadow-2xl" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setLightboxUrl(null)}
                            className="absolute -top-3 -right-3 bg-white border shadow-md text-gray-500 hover:text-gray-800 rounded-full p-1.5 transition-all active:scale-95 z-10"
                        >
                            <BsX size={20} />
                        </button>
                        <img
                            src={lightboxUrl}
                            alt="Visiting Card Preview"
                            className="max-w-full max-h-[80vh] rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default B2BLeadDetail;
